import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquarePlus, MessageCircle, X } from 'lucide-react';
import type { CommentAnchor, CommentStage, StageComment } from './api';
import './comment-anchors.css';

export const anchorQuote = (value: string) => value.replace(/\s+/g, ' ').trim().slice(0, 600);
/** Content fingerprint, not a version identifier: only used to reject stale coordinates. */
export function anchorRevision(value: unknown): string {
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  let hash = 2166136261;
  for (let i = 0; i < (text || '').length; i++) { hash ^= text.charCodeAt(i); hash = Math.imul(hash, 16777619); }
  return (hash >>> 0).toString(36);
}
export function sameAnchorBlock(a: CommentAnchor, b: CommentAnchor): boolean {
  return a.document_id === b.document_id && a.kind === b.kind && a.path === b.path && anchorQuote(a.quote || '') === anchorQuote(b.quote || '');
}
export interface CommentPinRef { id: string; anchor: CommentAnchor }
export function commentsAtBlock(pins: readonly CommentPinRef[], anchor: CommentAnchor): CommentPinRef[] {
  return pins.filter(pin => sameAnchorBlock(pin.anchor, anchor));
}
export function normalizedPoint(clientX: number, clientY: number, rect: { left: number; top: number; width: number; height: number }) {
  if (!rect.width || !rect.height) return null;
  const x = (clientX - rect.left) / rect.width, y = (clientY - rect.top) / rect.height;
  return x >= 0 && x <= 1 && y >= 0 && y <= 1 ? { x, y } : null;
}
type JumpStatus = 'found' | 'changed' | 'missing';
type Jump = (anchor: CommentAnchor) => JumpStatus | Promise<JumpStatus>;
interface AnchorWorkspace {
  stage: CommentStage; enabled: boolean; canWrite?: boolean; picking: boolean; pointOnly: boolean;
  setPicking: (value: boolean, pointOnly?: boolean) => void;
  anchor: CommentAnchor | null; clearAnchor: () => void;
  start: (anchor: CommentAnchor) => void;
  threads: StageComment[]; setThreads: (threads: StageComment[]) => void; replaceAnchors: (threads: StageComment[]) => void;
  focusedThreadId: string | null;
  openThread: (id: string) => void;
  register: (stage: CommentStage, id: string, jump: Jump) => () => void;
  jump: (anchor: CommentAnchor) => Promise<JumpStatus>;
}
const Context = createContext<AnchorWorkspace | null>(null);
export const CommentAnchorProvider = Context.Provider;
export const useCommentAnchors = () => useContext(Context);
export function useCommentWorkspace(stage: CommentStage, enabled: boolean, onOpen: () => void, canWrite = true): AnchorWorkspace {
  const [pickingStage, setPickingStage] = useState<CommentStage | null>(null);
  const [pointOnly, setPointOnly] = useState(false);
  const [drafts, setDrafts] = useState<Partial<Record<CommentStage, CommentAnchor | null>>>({});
  const [threadsByStage, setThreadsByStage] = useState<Partial<Record<CommentStage, StageComment[]>>>({});
  const [focusedThreadId, setFocusedThreadId] = useState<string | null>(null);
  const scopes = useRef(new Map<string, Jump>());
  const register = useCallback((s: CommentStage, id: string, handler: Jump) => {
    const key = `${s}:${id}`; scopes.current.set(key, handler);
    return () => { if (scopes.current.get(key) === handler) scopes.current.delete(key); };
  }, []);
  useEffect(() => { setPickingStage(null); setFocusedThreadId(null); }, [stage]);
  useEffect(() => {
    if (!pickingStage) return;
    const cancel = (event: KeyboardEvent) => { if (event.key === 'Escape') setPickingStage(null); };
    window.addEventListener('keydown', cancel);
    return () => window.removeEventListener('keydown', cancel);
  }, [pickingStage]);
  return useMemo(() => ({
    stage, enabled, canWrite, picking: canWrite && pickingStage === stage, pointOnly,
    setPicking: (value, point = false) => { setPickingStage(value && canWrite ? stage : null); setPointOnly(point); setFocusedThreadId(null); },
    anchor: drafts[stage] || null,
    clearAnchor: () => setDrafts(previous => ({ ...previous, [stage]: null })),
    start: anchor => { if (!enabled || !canWrite) return; setDrafts(previous => ({ ...previous, [stage]: anchor })); setPickingStage(null); setFocusedThreadId(null); onOpen(); },
    threads: threadsByStage[stage] || [],
    setThreads: threads => setThreadsByStage(previous => {
      const merged = new Map((previous[stage] || []).map(comment => [comment.id, comment]));
      for (const comment of threads) {
        if (comment.parent_id) continue;
        if (!comment.anchor || comment.resolved_at || comment.deleted) merged.delete(comment.id);
        else merged.set(comment.id, comment);
      }
      return { ...previous, [stage]: Array.from(merged.values()) };
    }),
    replaceAnchors: threads => setThreadsByStage(previous => ({ ...previous, [stage]: threads })),
    focusedThreadId,
    openThread: id => { setFocusedThreadId(id); setPickingStage(null); onOpen(); },
    register,
    jump: async anchor => scopes.current.get(`${stage}:${anchor.document_id}`)?.(anchor) || 'missing',
  }), [stage, enabled, canWrite, pickingStage, pointOnly, drafts, threadsByStage, focusedThreadId, register, onOpen]);
}
export function CommentPickingNotice() {
  const context = useCommentAnchors();
  if (!context?.picking) return null;
  return <div className="arch-comment-pick-notice" role="status"><MessageSquarePlus size={17} /><span>{context.stage === 'architecture' ? (context.pointOnly ? 'Tap the exact point you want to discuss. Drag to pan.' : 'Tap a block or any point on the architecture.') : 'Tap the section or row you want to discuss.'}</span><button type="button" onClick={() => context.setPicking(false)} aria-label="Cancel comment selection"><X size={17} /></button></div>;
}
export function commentBlockAttributes(anchor: CommentAnchor) {
  return { 'data-comment-anchor': JSON.stringify(anchor), 'data-comment-path': anchor.path };
}
export function useCommentDocument(stage: CommentStage, id: string, revision: string, prepare?: (anchor: CommentAnchor) => boolean) {
  const context = useCommentAnchors();
  const root = useRef<HTMLDivElement | null>(null);
  const prepareRef = useRef(prepare); prepareRef.current = prepare;
  const register = context?.register;
  useEffect(() => {
    if (!register) return;
    return register(stage, id, async anchor => {
      if (prepareRef.current?.(anchor)) await new Promise<void>(resolve => window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve())));
      const nodes = root.current?.querySelectorAll<HTMLElement>('[data-comment-anchor]') || [];
      let changed = false;
      for (const node of Array.from(nodes)) {
        const current: CommentAnchor = JSON.parse(node.dataset.commentAnchor || '{}');
        if (current.path !== anchor.path) continue;
        if (!sameAnchorBlock(current, anchor)) { changed = true; continue; }
        node.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        node.classList.remove('arch-comment-target'); void node.offsetWidth; node.classList.add('arch-comment-target');
        window.setTimeout(() => node.classList.remove('arch-comment-target'), 1800);
        return 'found';
      }
      return changed ? 'changed' : 'missing';
    });
  }, [register, stage, id, revision]);
  const anchor = (path: string, label: string, quote: string): CommentAnchor => ({ kind: 'block', document_id: id, revision, path, label: label.slice(0, 160), quote: anchorQuote(quote) });
  return {
    anchor,
    surface: {
      ref: root,
      'data-comment-selecting': context?.picking && context.stage === stage ? 'true' : undefined,
      onClickCapture: (event: React.MouseEvent) => {
        if (!context?.picking || context.stage !== stage) return;
        const node = (event.target as Element).closest<HTMLElement>('[data-comment-anchor]');
        if (!node || !root.current?.contains(node)) return;
        event.preventDefault(); event.stopPropagation();
        context.start(JSON.parse(node.dataset.commentAnchor || '{}'));
      },
    },
  };
}
export function CommentBlockButton({ anchor }: { anchor: CommentAnchor }) {
  const context = useCommentAnchors();
  if (!context?.enabled) return null;
  const matches = context.threads.filter(thread => !thread.parent_id && !thread.deleted && !thread.resolved_at && thread.anchor && sameAnchorBlock(thread.anchor, anchor));
  if (context.canWrite === false && !matches.length) return null;
  return <button type="button" className={'arch-block-comment' + (matches.length ? ' has-comments' : '')}
    title={matches.length ? `Open ${matches.length} discussion${matches.length > 1 ? 's' : ''} on ${anchor.label}` : `Comment on ${anchor.label}`}
    aria-label={matches.length ? `Open comments on ${anchor.label}` : `Comment on ${anchor.label}`}
    onClick={event => { event.stopPropagation(); if (matches.length) context.openThread(matches[0].id); else context.start(anchor); }}>
    {matches.length ? <MessageCircle size={15} /> : <MessageSquarePlus size={15} />}{matches.length ? <span>{matches.length}</span> : null}
  </button>;
}
